/**
 * Levels of Difficulty Service
 * Handles difficulty level metadata CRUD operations
 * Read operations are public, write operations require admin authorization
 */

import api from './apiClient';

/**
 * @typedef {Object} LevelOfDifficulty
 * @property {number} id - Level ID
 * @property {string} name - Level name (e.g., "Beginner", "Intermediate", "Advanced")
 * @property {string} hexColor - Hex color code (e.g., "#4CAF50")
 * @property {number} sortOrder - Display order
 * @property {string} dateCreated - Creation timestamp
 * @property {string} dateModified - Last modified timestamp
 */

/**
 * @typedef {Object} LevelOfDifficultyPagedResult
 * @property {LevelOfDifficulty[]} items - Array of levels
 * @property {number} totalCount - Total number of levels
 * @property {number} page - Current page number
 * @property {number} pageSize - Items per page
 * @property {number} totalPages - Total number of pages
 * @property {boolean} hasNextPage - Whether more pages exist
 * @property {boolean} hasPreviousPage - Whether previous pages exist
 */

/**
 * @typedef {Object} LevelOfDifficultyQueryParams
 * @property {number} [page=1] - Page number
 * @property {number} [pageSize=50] - Items per page
 * @property {string} [search] - Search in name
 */

/**
 * @typedef {Object} CreateLevelOfDifficultyDto
 * @property {string} name - Level name (required)
 * @property {string} hexColor - Hex color code (required)
 * @property {number} [sortOrder=0] - Display order
 */

/**
 * @typedef {Object} UpdateLevelOfDifficultyDto
 * @property {string} [name] - Level name
 * @property {string} [hexColor] - Hex color code
 * @property {number} [sortOrder] - Display order
 */

/**
 * Get all difficulty levels (no pagination, for dropdowns)
 * @param {Object} [options] - Request options
 * @returns {Promise<LevelOfDifficulty[]>}
 */
export const getAllLevels = async (options = {}) => {
  return api.get('/levelsofdifficulty/all', {}, options);
};

/**
 * Get paginated list of difficulty levels
 * @param {LevelOfDifficultyQueryParams} [params={}] - Query parameters
 * @param {Object} [options] - Request options
 * @returns {Promise<LevelOfDifficultyPagedResult>}
 */
export const getLevels = async (params = {}, options = {}) => {
  return api.get('/levelsofdifficulty', params, options);
};

/**
 * Get a single difficulty level by ID
 * @param {number} id - Level ID
 * @param {Object} [options] - Request options
 * @returns {Promise<LevelOfDifficulty>}
 */
export const getLevel = async (id, options = {}) => {
  return api.get(`/levelsofdifficulty/${id}`, {}, options);
};

/**
 * Get a difficulty level by its name
 * @param {string} name - Level name
 * @param {Object} [options] - Request options
 * @returns {Promise<LevelOfDifficulty>}
 */
export const getLevelByName = async (name, options = {}) => {
  return api.get(`/levelsofdifficulty/name/${encodeURIComponent(name)}`, {}, options);
};

/**
 * Create a new difficulty level (requires admin)
 * @param {CreateLevelOfDifficultyDto} data - Level data
 * @param {Object} [options] - Request options
 * @returns {Promise<LevelOfDifficulty>}
 */
export const createLevel = async (data, options = {}) => {
  return api.post('/levelsofdifficulty', data, options);
};

/**
 * Update an existing difficulty level (requires admin)
 * @param {number} id - Level ID
 * @param {UpdateLevelOfDifficultyDto} data - Updated level data
 * @param {Object} [options] - Request options
 * @returns {Promise<LevelOfDifficulty>}
 */
export const updateLevel = async (id, data, options = {}) => {
  return api.put(`/levelsofdifficulty/${id}`, data, options);
};

/**
 * Delete a difficulty level (requires admin)
 * @param {number} id - Level ID
 * @param {Object} [options] - Request options
 * @returns {Promise<void>}
 */
export const deleteLevel = async (id, options = {}) => {
  return api.delete(`/levelsofdifficulty/${id}`, options);
};

/**
 * Validates hex color format
 * @param {string} hexColor - Color to validate
 * @returns {boolean} Whether the color is valid
 */
export const isValidHexColor = (hexColor) => {
  if (!hexColor || typeof hexColor !== 'string') {
    return false;
  }
  const color = hexColor.trim();
  // Match #RRGGBB or RRGGBB format
  return /^#?[0-9A-Fa-f]{6}$/.test(color);
};

/**
 * Normalizes hex color to include # prefix
 * @param {string} hexColor - Color to normalize
 * @returns {string} Normalized color with # prefix
 */
export const normalizeHexColor = (hexColor) => {
  if (!hexColor) return '';
  const color = hexColor.trim();
  return color.startsWith('#') ? color : `#${color}`;
};

/**
 * Default export with all level of difficulty service methods
 */
export default {
  getAllLevels,
  getLevels,
  getLevel,
  getLevelByName,
  createLevel,
  updateLevel,
  deleteLevel,
  isValidHexColor,
  normalizeHexColor,
};
